import { BulkUpdatePopOver } from '@components/UI-Components/BulkUpdatePopOver'
import { Label } from '@components/UI-Components/Label'
import { ListViewDropDown } from '@components/UI-Components/ListViewDropDown'
import { ListViewMenu } from '@components/UI-Components/ListViewMenu'
import { OrderManagementActions } from '@components/UI-Components/OrderManagementActions'
import { DeletedOrderManagementActions } from '@components/UI-Components/DeletedOrderManagementActions'
import { StandardLink } from '@components/UI-Components/StandardLink'
import { TableViewColumnDefinition } from '@components/UI-Components/StandardTableView/models/TableViewColumnDefinition'
import { StatusComponent } from '@components/UI-Components/StatusComponent'
import { config } from '@config'
import { FontTShirtSize } from '@modules/common/models/enums/FontSize'
import DeliveryDatePicker from '@modules/showroom/basketAndOrders-new/components/CellTemplates/DeliveryDate/DeliveryDatePicker'
import { stores, strings } from '@stores'
import get from 'lodash/get'
import intersectionBy from 'lodash/intersectionBy'
import { useObserver } from 'mobx-react-lite'
import moment from 'moment'
import React from 'react'
import {
  getFilteredData,
  getInstructionText,
  getStatusData,
  getStatusesList,
  IOrderManagementTableData,
  isDisabledBulkSendToSAPIcon,
  isDisabledBulkStatusIcon
} from '@services/wholesale/OrderManagementService'
import { bulkSendToSAPOptions } from './constant'
import * as css from './OrderManagementWrapper.css'
import { getDateFormatByLocale } from '@utils/get-date-format-by-locale'
import { Translation } from 'react-i18next'
import qs from 'query-string'
import { OrderManagementSection } from '@modules/common/models/enums/OrderManagement'
import { View } from '@modules/order/constants/views'

const MAX_APPLY_LIMIT_FOR_BULK_SEND_TO_SAP = 100
const MAX_APPLY_LIMIT_FOR_BULK_STATUS_CHANGE = 20

const ActionsComponent = ({ rowData, columnDefinition }) =>
  useObserver(function useHook() {
    const {
      orderManagementStore: {
        deletedOrdersTabView,
        onDeleteIconClick,
        onRestoreIconClick,
        onExportToPDF,
        inProcessOrderPDF
      }
    } = stores

    if (deletedOrdersTabView) {
      return (
        <DeletedOrderManagementActions
          onRestore={onRestoreIconClick(rowData.id, rowData.orderType)}
          disabledRestore={rowData[columnDefinition.node.disableKey]}
        />
      )
    }
    return (
      <OrderManagementActions
        orderNumber={rowData.orderNumber}
        onDelete={onDeleteIconClick(rowData.id, rowData.orderType)}
        disabledDelete={rowData[columnDefinition.node.disableKey]}
        onExportToPDF={onExportToPDF(rowData.id, rowData.orderType)}
        disablePDFExport={rowData.disablePDFExport}
        isPDFExportInProcess={inProcessOrderPDF[rowData.id]}
      />
    )
  })

const SendToSapContentComponent = ({ rowData, columnDefinition }) =>
  useObserver(function useHook() {
    const {
      orderManagementStore: {
        sendOrderToSAP,
        sendToSAPInProgressMap,
        deletedOrdersTabView
      }
    } = stores
    return (
      <StatusComponent
        onClick={
          rowData.canSendToSap ? sendOrderToSAP([rowData], rowData.orderType) : null
        }
        value={get(rowData, columnDefinition.node.key)}
        isBold={rowData.isSapStatusBold}
        fontColor={rowData.sapStatusColor}
        disabled={
          deletedOrdersTabView ||
          sendToSAPInProgressMap[rowData.id] ||
          rowData[columnDefinition.node.disableKey]
        }
        {...columnDefinition.props}
      />
    )
  })

class OrderManagementComponentService {
  getLabelComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      orderManagementStore: { deletedOrdersTabView }
    } = stores
    return (
      <Label
        fontSize={FontTShirtSize.sm}
        cellPadding={'0px'}
        value={rowData[columnDefinition.node.key]}
        disabled={
          deletedOrdersTabView || rowData[columnDefinition.node.disableKey] || false
        }
        {...columnDefinition.props}
      />
    )
  }

  getLinkComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      nav: {
        queryParams,
        location: { pathname }
      }
    } = stores

    const params = {
      orderId: rowData?.id,
      clientId: rowData?.clientId,
      orderType: rowData?.orderType,
      clientOrderId: rowData?.clientOrderId ?? undefined,
      buyingSession: rowData?.buyingSessionId ?? undefined,
      clientType:
        queryParams?.section === OrderManagementSection.retailOrder ? 'Internal' : '',
      view: View.Product,
      toolbar: 'hide',
      zone: queryParams?.view
    }

    return (
      <StandardLink
        fontSize={FontTShirtSize.xxs}
        value={rowData[columnDefinition.node.key]}
        disabled={rowData[columnDefinition.node.disableKey] || false}
        to={{
          pathname,
          search: qs.stringify({ ...queryParams, ...params })
        }}
        {...columnDefinition.props}
      ></StandardLink>
    )
  }

  getMenuComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      orderManagementStore: { deletedOrdersTabView }
    } = stores
    const data = get(rowData, columnDefinition.node.key) || []
    return (
      <ListViewMenu
        label={data.length > 1 ? strings.multiple : data?.[0]?.['label'] ?? 'NA'}
        values={data}
        disableAction={deletedOrdersTabView}
        {...columnDefinition.props}
      />
    )
  }

  /**
   * Function for returning status component
   * NOTE: This is not generic function and can be used only for order management table
   * @param {IOrderManagementTableData} rowData
   * @param {TableViewColumnDefinition} columnDefinition
   */
  getStatusComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    let valueColorMap =
      config?.appConfig?.enumerations?.orderStatus?.VALUE_COLOR_MAP ?? {}
    const {
      orderManagementStore: { onOrderStatusChange, deletedOrdersTabView }
    } = stores
    return (
      <ListViewDropDown
        label={rowData[columnDefinition.node.key]}
        values={rowData.possibleUpdateOptionsForStatus || []}
        disabled={
          deletedOrdersTabView || rowData[columnDefinition.node.disableKey] || false
        }
        borderColor={valueColorMap[rowData.statusEnumValue]}
        onChange={onOrderStatusChange(rowData)}
      />
    )
  }

  getDeliveryDateComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      orderManagementStore: { onOrderDeliveryDateChange, deletedOrdersTabView }
    } = stores
    // Date will be in dd/mm/yyyy format which cannot be passed to datepicker.
    // Need to convert it to yyyy-mm-dd format
    let deliveryDateValue = rowData[columnDefinition.node.key]
    if (deliveryDateValue) {
      deliveryDateValue = moment(deliveryDateValue, 'DD/MM/YYYY').format('YYYY-MM-DD')
    }

    return (
      <DeliveryDatePicker
        deliveryDate={deliveryDateValue}
        onOrderDeliveryDateChange={onOrderDeliveryDateChange(
          rowData.id,
          rowData.orderType,
          true
        )}
        classes={{
          deliveryDatePickerContainer: css.deliveryDatePickerContainer,
          root: css.deliveryDatePicker,
          deliveryDateLabel: css.deliveryDateLabel,
          inputRoot: css.deliveryDatePickerRoot,
          disabledLabel: css.disabledLabel
        }}
        buyingGroupDeliveryWindow={rowData.deliveryWindow}
        disableControls={
          deletedOrdersTabView || rowData[columnDefinition.node.disableKey] || false
        }
        dateFormat={getDateFormatByLocale()}
      />
    )
  }

  getActionsComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    return <ActionsComponent rowData={rowData} columnDefinition={columnDefinition} />
  }

  getSendToSAPComponent = (
    rowData: IOrderManagementTableData,
    columnDefinition: TableViewColumnDefinition
  ) => {
    return (
      <SendToSapContentComponent rowData={rowData} columnDefinition={columnDefinition} />
    )
  }
  getBulkUpdateComponentForStatusChange = (
    data,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      orderManagementStore: { deletedOrdersTabView }
    } = stores
    let valueColorMap =
      config?.appConfig?.enumerations?.orderStatus?.VALUE_COLOR_MAP ?? {}
    const {
      orderManagementStore: { bulkStatusChange, isStatusChangeInProgress }
    } = stores
    const rowData =
      data?.filteredRows && data?.filteredRows.length && data.filteredRows[0]
    const { status, possibleUpdateOptionsForStatus, statusEnumValue } = getStatusData(
      rowData,
      columnDefinition?.node?.key
    )
    const { filteredData, selectedData } = getFilteredData(
      data.filteredRows,
      data.selectedRows
    )
    const disabled = isDisabledBulkStatusIcon(
      data?.filteredRows,
      columnDefinition?.node?.key,
      status,
      filteredData,
      possibleUpdateOptionsForStatus,
      deletedOrdersTabView
    )
    const statusesList = getStatusesList(possibleUpdateOptionsForStatus)
    const dataForApplyToSelected = intersectionBy(filteredData, selectedData, 'id')
    return (
      <Translation>
        {t => (
          <BulkUpdatePopOver
            data={filteredData}
            selectedData={dataForApplyToSelected}
            noCustomHeight={true}
            width={365}
            subtitle={t(
              'bb.common.label.orderManagementBulkSubtitle',
              'Bulk status change'
            )}
            applyToInstruction={getInstructionText(
              MAX_APPLY_LIMIT_FOR_BULK_STATUS_CHANGE,
              filteredData
            )}
            options={statusesList}
            status={status}
            statusColor={valueColorMap[statusEnumValue]}
            onApplyClick={bulkStatusChange}
            isInProgress={isStatusChangeInProgress}
            disabled={disabled}
            tooltip={
              disabled
                ? t(
                    'bb.common.warnings.bulkStatusChangeTooltip',
                    'Either filter is not applied or more than, one status is selected in filter, or selected status cannot be updated.'
                  )
                : ''
            }
            actionForMultiSelect={bulkStatusChange}
            clearTableFilter={data?.clearFilters}
            maxApplyLimit={MAX_APPLY_LIMIT_FOR_BULK_STATUS_CHANGE}
          />
        )}
      </Translation>
    )
  }
  getOrderManagementBulkStatusComponentForOpen = (
    data,
    columnDefinition: TableViewColumnDefinition
  ) => {
    const {
      orderManagementStore: {
        sendToSAPBulkInProgress,
        sendOrderToSAP,
        deletedOrdersTabView
      }
    } = stores
    const { filteredData, selectedData } = getFilteredData(
      data.filteredRows,
      data.selectedRows
    )
    const disabled = isDisabledBulkSendToSAPIcon(data?.filteredRows, filteredData)
    const dataForApplyToSelected = intersectionBy(filteredData, selectedData, 'id')
    return (
      <Translation>
        {t => (
          <BulkUpdatePopOver
            data={filteredData}
            selectedData={dataForApplyToSelected}
            noCustomHeight={true}
            width={365}
            applyToInstruction={getInstructionText(
              MAX_APPLY_LIMIT_FOR_BULK_SEND_TO_SAP,
              filteredData
            )}
            title={t('bb.common.label.clientBulkChangeTitle', 'Bulk Change')}
            options={bulkSendToSAPOptions}
            onApplyClick={sendOrderToSAP}
            isInProgress={sendToSAPBulkInProgress}
            isCheckboxOptions={true}
            actionForMultiSelect={sendOrderToSAP}
            clearTableFilter={data?.clearFilters}
            disabled={disabled || deletedOrdersTabView}
            maxApplyLimit={MAX_APPLY_LIMIT_FOR_BULK_SEND_TO_SAP}
          />
        )}
      </Translation>
    )
  }
}

export const orderManagementComponentService = new OrderManagementComponentService()
